import { useForm, Controller } from "react-hook-form";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { ClipboardCopy, EditIcon, Trash2 } from "lucide-react";
import {
  useDeleteFavoriteQueryMutation,
  useUpdateFavoriteQueryMutation,
} from "@/services/it/updates/query-favorites";
import { MESSAGES } from "../../utils/messages";
import { toast } from "components/ui/use-toast";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DeleteConfirmationModal } from "./delete-confirmation-modal";
import { useExecuteQueryPreviewMutation } from "@/services/it/updates/query-update";
import { useState } from "react";
import { QueryResponseData } from "@/@types/query-response";
import { SelectResultsDialog } from "../query-request/select/result-dialog";
import { EditDataPreviewModal } from "./edit-preview-modal";
import { FaPlay } from "react-icons/fa6";

export function QueryEdit({
  queryId,
  query,
  query_nickname,
}: {
  queryId: number;
  query: string;
  query_nickname: string;
}) {
  const [open, setOpen] = useState(false);
  const [modalData, setModalData] = useState<QueryResponseData | null>(null);
  const [isModalOpen, setModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [isDialogOpen, setDialogOpen] = useState(!!modalData);
  const { control, handleSubmit } = useForm({
    defaultValues: {
      query: query || "",
      query_nickname: query_nickname || "",
    },
  });
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const [executeQueryPreview, { isLoading: IsPreviewLoading }] =
    useExecuteQueryPreviewMutation();
  const [deleteFavoriteQuery, { isLoading: deleteLoading }] =
    useDeleteFavoriteQueryMutation();
  const [updateFavoriteQuery, { isLoading: updateLoading }] =
    useUpdateFavoriteQueryMutation();

  const handleCopy = async (
    event: React.MouseEvent,
    query: string,
    id: number
  ) => {
    event.stopPropagation();
    setCopiedId(id);
    await navigator.clipboard.writeText(query);
    setTimeout(() => setCopiedId(null), 1000);
  };

  const handleSave = handleSubmit(async (data) => {
    const { query, query_nickname } = data;
    try {
      await updateFavoriteQuery({
        id: queryId,
        query: query,
        query_nickname: query_nickname,
      }).unwrap();
      toast(MESSAGES.EDIT_QUERY.SUCCESS);
      setOpen(false);
    } catch {
      toast(MESSAGES.EDIT_QUERY.ERROR);
    }
  });

  const confirmDelete = async () => {
    try {
      await deleteFavoriteQuery({ id: queryId }).unwrap();
      toast(MESSAGES.DELETE_QUERY.SUCCESS);
      setDeleteModalOpen(false);
      setOpen(false);
    } catch {
      toast(MESSAGES.DELETE_QUERY.ERROR);
    }
  };

  const connectionId = 1;

  const handlePreviewSubmit = async (query: string) => {
    try {
      const result = await executeQueryPreview({
        query,
        connectionId,
      }).unwrap();

      if (
        result === `Comandos do tipo 'TRUNCATE' ou 'DROP' não são permitidos.`
      ) {
        toast(MESSAGES.QUERY_ERROR.ERROR);
        return;
      }

      console.log(result.length === undefined);
      if (
        result ===
        `Comandos do tipo 'UPDATE' ou 'DELETE' sem 'WHERE' não são permitidos.`
      ) {
        toast(MESSAGES.NO_WHERE.ERROR);
        return;
      }

      if (!result.result_before && !result) {
        alert("Essa query não modificou nenhum dado, revise-a!");
        return;
      }

      setModalData(result);
      console.log(modalData?.type);
      setModalOpen(true);
      setErrorMessage("");
      toast(MESSAGES.CREATE.SUCCESS);
    } catch (error: any) {
      if (error?.status === 500) {
        setErrorMessage(error?.data?.messages[0]);
      } else {
        console.error("Algo não saiu como esperado!", error);
        setErrorMessage("Erro inesperado ao executar a query.");
      }
      console.log(error);
      toast(MESSAGES.CREATE.ERROR);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <button className="text-black bg-[#E9E9EF] hover:bg-[#E9E9EF]/40 px-2 py-2 rounded border transition-all ease-in-out duration-300">
            <EditIcon size={16} className="cursor-pointer" />
          </button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-[565px] lg:max-w-[700px] bg-white">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">
              Editar Query Favorita
            </DialogTitle>
            <DialogDescription>
              Personalize sua query favorita ajustando o apelido e o conteúdo
              conforme necessário.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="w-full space-y-4">
            <div className="space-y-2">
              <Label htmlFor="query_nickname" className="text-sm font-medium">
                Apelido da Query
              </Label>
              <Controller
                name="query_nickname"
                control={control}
                render={({ field }) => (
                  <Input
                    id="query_nickname"
                    {...field}
                    placeholder="Digite um apelido para sua query"
                    disabled={deleteLoading || updateLoading}
                    className="w-full"
                  />
                )}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="query-content" className="text-sm font-medium">
                Conteúdo da Query
              </Label>
              <div className="relative w-full">
                <Controller
                  name="query"
                  control={control}
                  render={({ field }) => (
                    <Textarea
                      id="query-content"
                      className="font-mono min-h-[200px] w-full bg-muted/50 resize-none p-4"
                      {...field}
                      disabled={deleteLoading || updateLoading}
                      placeholder="Digite ou cole sua query aqui"
                    />
                  )}
                />

                <div className="absolute bottom-2 right-2 flex items-center space-x-2">
                  {copiedId === queryId && (
                    <p className="text-sm text-[#fff] bg-black px-2 py-1 rounded">
                      Copiado
                    </p>
                  )}
                  <button
                    type="button"
                    className="bg-[#000000] text-white px-3 py-1 rounded hover:bg-[#000000]/80 transition-all ease-in-out duration-300"
                    onClick={(e) => handleCopy(e, query, queryId)}
                  >
                    <ClipboardCopy size={20} />
                  </button>
                </div>
              </div>
            </div>
          </form>
          <DialogFooter className="flex flex-row flex-wrap justify-between sm:justify-between">
            <div>
              <Button
                disabled={deleteLoading || updateLoading}
                variant="destructive"
                className="rounded-md flex justify-self-start"
                onClick={() => setDeleteModalOpen(true)}
              >
                <Trash2 size={16} className="mr-2" /> Excluir
              </Button>
            </div>
            <div className="flex space-x-2">
              <DialogClose asChild>
                <Button variant="outline" className="rounded-md">
                  Cancelar
                </Button>
              </DialogClose>
              <Button
                onClick={handleSave}
                disabled={deleteLoading || updateLoading}
                className="bg-black text-white rounded-md font-semibold p-2 px-4 hover:bg-black/80"
              >
                Salvar
              </Button>
            </div>
          </DialogFooter>
          {modalData?.type === "SELECT" ? (
            <div className="w-full">
              <SelectResultsDialog
                query={query}
                modalData={modalData}
                isDialogOpen={isDialogOpen}
                setDialogOpen={setDialogOpen}
              />
            </div>
          ) : (
            modalData &&
            isModalOpen && (
              <EditDataPreviewModal
                query={query}
                connectionId={connectionId}
                data={modalData}
                onClose={() => setModalOpen(false)}
              />
            )
          )}
          {errorMessage && (
            <span className="border p-2 text-sm rounded-md">
              {errorMessage}
            </span>
          )}
          <Button
            onClick={() => handlePreviewSubmit(query)}
            disabled={IsPreviewLoading || query.length === 0}
            className="bg-black text-white rounded-md font-[550] text-sm p-2 px-4 flex items-center gap-2 hover:bg-black/80 transition-all ease-in-out duration-300"
          >
            <FaPlay />
            {IsPreviewLoading ? "Carregando..." : "Executar Query"}
          </Button>
        </DialogContent>
      </Dialog>
      <DeleteConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        isLoading={deleteLoading}
      />
    </>
  );
}
